import { memo, useEffect, useMemo } from "react";
import { FieldValues, Path, PathValue, useFormContext } from "react-hook-form";
import DataTableCellField from "@/components/data-table/components/DataTableCellField";

interface DataTableFormulaInputFieldProps<TFormValues extends FieldValues> {
  name: string;
  formula: (inputValues: Record<keyof TFormValues, string>) => number;
  watchFields: (keyof TFormValues)[];
  getFieldPath: (fieldName: keyof TFormValues) => string;
  disabled?: boolean;
  type?: "input" | "checkbox" | "number";
  className?: string;
  wrapperClassName?: string;
}

/**
 * A data table field that automatically calculates its value based on other form fields.
 *
 * This component watches specified form fields and recalculates its value whenever
 * any of the watched fields change. The calculated value is automatically set in the form
 * without triggering dirty/touched/validation states.
 *
 * @example
 * ```tsx
 * <DataTableFormulaInputField
 *   name="total"
 *   formula={(inputs) => parseFloat(inputs.price) * parseFloat(inputs.quantity)}
 *   watchFields={['price', 'quantity']}
 *   getFieldPath={(fieldName) => `items.${index}.${fieldName}`}
 * />
 * ```
 *
 * @template TFormValues - The type of the form values
 */
const DataTableFormulaInputField = memo(
  <TFormValues extends FieldValues>({
    name,
    formula,
    watchFields,
    getFieldPath,
    type = "input",
    className,
    wrapperClassName,
  }: DataTableFormulaInputFieldProps<TFormValues>) => {
    const { watch, setValue, getValues } = useFormContext<TFormValues>();

    const fieldPaths = useMemo(
      () => watchFields.map(getFieldPath) as Path<TFormValues>[],
      [watchFields, getFieldPath],
    );

    const watchedValues = watch(fieldPaths);

    const calculatedValue = useMemo(() => {
      const inputValues = watchFields.reduce(
        (acc, fieldName, index) => {
          acc[fieldName] = (watchedValues[index] as string) || "0";
          return acc;
        },
        {} as Record<keyof TFormValues, string>,
      );

      return formula(inputValues);
    }, [watchedValues, formula, watchFields]);

    useEffect(() => {
      const currentValue = getValues(name as Path<TFormValues>);
      // Align with laborCostStoreNumericSchema (3 dp) so we don’t fight string values from NumberInput.
      const nextSerialized = Number.isFinite(calculatedValue)
        ? Number(calculatedValue).toFixed(3)
        : "0.000";
      if (String(currentValue ?? "") !== nextSerialized) {
        setValue(
          name as Path<TFormValues>,
          nextSerialized as PathValue<TFormValues, Path<TFormValues>>,
          {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          },
        );
      }
    }, [calculatedValue, setValue, name, getValues]);

    return (
      <DataTableCellField
        name={name as string}
        type={type}
        className={className}
        wrapperClassName={wrapperClassName}
      />
    );
  },
) as <TFormValues extends FieldValues>(
  props: DataTableFormulaInputFieldProps<TFormValues>,
) => JSX.Element;

export default DataTableFormulaInputField;
